import { useCallback, useEffect, useRef, useState } from "react";
import { Briefcase, Check } from "lucide-react";
import { searchNoc } from "@/lib/api";

function useNocSearch(value, skipRef) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return; }
    if (!value || value.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => {
      searchNoc(value).then((res) => { setItems(res); setOpen(res.length > 0); setActive(-1); }).catch(() => setItems([]));
    }, 180);
    return () => clearTimeout(t);
  }, [value, skipRef]);
  return { items, setItems, open, setOpen, active, setActive };
}

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onOutside(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ref, onOutside]);
}

const KEY_HANDLERS = {
  ArrowDown: ({ setActive, items }) => setActive((a) => Math.min(items.length - 1, a + 1)),
  ArrowUp: ({ setActive }) => setActive((a) => Math.max(0, a - 1)),
  Enter: ({ active, items, pick }) => active >= 0 && pick(items[active]),
  Escape: ({ setOpen }) => setOpen(false),
};

function NocOption({ item, active, onPick, onHover }) {
  return (
    <li role="option" aria-selected={active} data-testid={`noc-option-${item.code}`}
      onMouseDown={(e) => { e.preventDefault(); onPick(item); }} onMouseEnter={onHover}
      className={`flex cursor-pointer items-start gap-3 px-3.5 py-2.5 text-sm ${active ? "bg-[#FEF2F2]" : ""}`}>
      <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <span className="flex-1 text-slate-800">{item.title}</span>
      <span className="font-mono-tech text-xs text-slate-500">{item.code} · TEER {item.teer}</span>
    </li>
  );
}

export function NocAutocomplete({ value, nocCode, onChange, onPick, className }) {
  const wrapRef = useRef(null);
  const skipRef = useRef(false);
  const search = useNocSearch(value, skipRef);
  const { items, setItems, open, setOpen, active, setActive } = search;
  useClickOutside(wrapRef, useCallback(() => setOpen(false), [setOpen]));

  const pick = (item) => {
    skipRef.current = true;
    onPick(item);
    setOpen(false);
    setItems([]);
  };

  const onKey = (e) => {
    const handler = open && items.length ? KEY_HANDLERS[e.key] : null;
    if (!handler) return;
    if (e.key !== "Escape") e.preventDefault();
    handler({ ...search, pick });
  };

  return (
    <div ref={wrapRef} className="relative">
      <input type="text" value={value || ""} data-testid="input-occupation-title" autoComplete="off"
        placeholder="e.g. Software Developer" className={className}
        onChange={(e) => onChange(e.target.value)} onFocus={() => items.length && setOpen(true)} onKeyDown={onKey}
        role="combobox" aria-expanded={open} aria-autocomplete="list" />
      {nocCode && (
        <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700" data-testid="noc-match">
          <Check className="h-3.5 w-3.5" /> NOC {nocCode} matched · TEER {nocCode[1]} auto-filled
        </p>
      )}
      {open && (
        <ul role="listbox" data-testid="noc-suggestions" className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {items.map((it, i) => <NocOption key={it.code} item={it} active={i === active} onPick={pick} onHover={() => setActive(i)} />)}
        </ul>
      )}
    </div>
  );
}
